import { useEffect, useRef } from 'react';
import { isSupportSession } from '@/auth/supportSession';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { sendNotification } from '@srm/ui/lib/notifications';
import { useScreenShareContext } from '@providers/ScreenShareProvider/ScreenShareContext';
import { announceSupport } from '@providers/ScreenShareProvider/supportAnnouncer';
import SupportRequestBar from './SupportRequestBar';
import { useSupportRequests } from './useSupportRequests';
import { supportRequestDetails, supportRequestTitle } from './supportRequestText';

/**
 * Запросы поддержки к пользователю в зоне `.notes` оболочки. Во вкладке «от имени» их нет: отвечать за пользователя
 * сотрудник не может (сервер эти запросы там и не отдаёт).
 */
export default function SupportRequestBars() {
    if (isSupportSession()) return null;
    return <ActiveSupportRequestBars />;
}

function ActiveSupportRequestBars() {
    const authenticated = useTypedSelector((state) => state.auth.isAuth);
    const userId = useTypedSelector((state) => state.auth.user?.id);
    const share = useScreenShareContext();
    const { requests, now, busy, reload, accept, decline } = useSupportRequests(authenticated && !!userId);

    // Пока экран показывается, приглашения не нужны: показ один, а принятое приглашение сервер уже снял.
    const visible = requests.filter((request) => request.kind === 'consent' || (share.available && !share.active));

    // Новое приглашение — диктору через постоянную live-область (согласие объявляет своя плашка, role="alert").
    const announced = useRef(new Set<string>());
    useEffect(() => {
        visible.forEach((request) => {
            if (request.kind !== 'invite' || announced.current.has(request.id)) return;
            announced.current.add(request.id);
            const details = supportRequestDetails(request);
            announceSupport(details ? `${supportRequestTitle(request)}. ${details}` : supportRequestTitle(request));
        });
    });

    if (visible.length === 0) return null;

    return (
        <>
            {visible.map((request) => (
                <SupportRequestBar
                    key={request.id}
                    request={request}
                    now={now}
                    busy={busy[request.id]}
                    onAccept={() => {
                        if (request.kind === 'invite') {
                            // Выбор вкладки — в этом же нажатии; приглашение принимает хаб в StartWith (§16).
                            void share.start({ inviteId: request.id }).finally(() => void reload());
                            return;
                        }
                        void accept(request.id).then((done) => {
                            if (done)
                                sendNotification({
                                    title: 'Доступ разрешён',
                                    message: `${request.actorName || 'Сотрудник поддержки'} сможет войти в вашу учётную запись. Сеанс будет виден в «Доступе поддержки».`,
                                    variant: 'green',
                                });
                        });
                    }}
                    onDecline={() => void decline(request.id)}
                />
            ))}
        </>
    );
}
